import * as Sentry from '@sentry/browser';
import { useQuery } from '@tanstack/react-query';
import type {
  PositionFilter,
  PositionsFormFieldQueryQuery,
  PositionsFormFieldQueryQueryVariables,
} from '__generated__/graphql';
import { positionsQuery } from 'components/domain/position/PositionFormField/position-form-field-queries';
import { useToastOpen } from 'contexts/ToastContext';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { isNil } from 'lodash';
import { t } from 'utils/localize';

interface IPositionFormFieldQueryVariableOptions {
  showAllPositions?: boolean;
  positionLevel?: number;
}
function getPositionFormFieldQueryVariables({
  showAllPositions,
  positionLevel,
}: IPositionFormFieldQueryVariableOptions) {
  const filter: PositionFilter = {
    deletedOn: { isNull: true },
  };

  if (!isNil(positionLevel) && !showAllPositions) {
    filter.level = { greaterThanOrEqual: positionLevel };
  }

  return { filter };
}

function useFetchPositionFormFieldPositions() {
  const graphqlClient = useGraphQLClient();
  const toastOpen = useToastOpen();

  return (options: IPositionFormFieldQueryVariableOptions) => {
    const requestConfig = {
      document: positionsQuery,
      variables: getPositionFormFieldQueryVariables(options),
    };
    return graphqlClient
      .request<PositionsFormFieldQueryQuery, PositionsFormFieldQueryQueryVariables>(requestConfig)
      .then((result) => result.positions ?? [])
      .catch((error: unknown) => {
        toastOpen({ label: t('Failed to load positions.') });
        Sentry.captureException(error, { extra: { requestConfig, options } });
        // eslint-disable-next-line no-console
        console.error('Error fetching positions:', { error, requestConfig });

        return [];
      });
  };
}

function usePositionFormFieldPositionsQueryKey(options: IPositionFormFieldQueryVariableOptions) {
  return [...useReactQueryBaseKey(), 'PositionFormFieldPositionsQuery', options];
}

export function usePositionFormFieldPositionsQuery(options: IPositionFormFieldQueryVariableOptions) {
  const fetchPositions = useFetchPositionFormFieldPositions();

  return useQuery({
    queryKey: usePositionFormFieldPositionsQueryKey(options),
    queryFn: () => fetchPositions(options),
    enabled: options.showAllPositions || !isNil(options.positionLevel),
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 0,
    gcTime: 0,
  });
}
